import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";

import { FPBreadcrumb } from "@/components/FPLayout/FPBreadcrumbs";

type SetBreadcrumbs = (breadcrumbs: FPBreadcrumb[]) => void;

/**
 * Split into two contexts so that components calling useSetBreadcrumbs
 * (the dispatch context) don't re-render when the breadcrumb state changes.
 */
const BreadcrumbStateContext = createContext<FPBreadcrumb[]>([]);
const BreadcrumbDispatchContext = createContext<SetBreadcrumbs | null>(null);

export function NavigationLayoutProvider({
  children,
}: PropsWithChildren<unknown>) {
  const [breadcrumbs, setBreadcrumbsState] = useState<FPBreadcrumb[]>([]);

  const setBreadcrumbs = useCallback((b: FPBreadcrumb[]) => {
    setBreadcrumbsState(b);
  }, []);

  return (
    <BreadcrumbDispatchContext.Provider value={setBreadcrumbs}>
      <BreadcrumbStateContext.Provider value={breadcrumbs}>
        {children}
      </BreadcrumbStateContext.Provider>
    </BreadcrumbDispatchContext.Provider>
  );
}

export function useNavigationLayout() {
  const breadcrumbs = useContext(BreadcrumbStateContext);
  return { breadcrumbs };
}

/**
 * Syncs page-specific breadcrumbs into the persistent navigation shell.
 * Call from page components that need to display breadcrumbs.
 */
export function useSetBreadcrumbs(breadcrumbs: FPBreadcrumb[]): void {
  const setBreadcrumbs = useContext(BreadcrumbDispatchContext);
  const serialized = JSON.stringify(breadcrumbs);

  useEffect(() => {
    setBreadcrumbs?.(breadcrumbs);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setBreadcrumbs, serialized]);
}
